import { z } from 'zod';
import type { ChatMessage } from '@reados/contracts';
import type { TranscriptPayload } from './projection';

const offset = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
export const selectionRectSchema = z.object({
  left: z.number().finite().min(-10_000_000).max(10_000_000),
  top: z.number().finite().min(-10_000_000).max(10_000_000),
  width: z.number().finite().min(0).max(10_000_000),
  height: z.number().finite().min(0).max(10_000_000),
}).strict();
export type SelectionRect = z.infer<typeof selectionRectSchema>;
export interface TranscriptSelectionAnchor {
  getRect: () => SelectionRect | null;
  subscribe: (listener: () => void) => () => void;
}
/** Geometry belongs to this live iframe, never to a saved annotation. */
export function createSelectionAnchor(frame: () => HTMLIFrameElement | null, initial: SelectionRect, watch: (active: boolean) => void) {
  let rect: SelectionRect | null = initial;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach(listener => listener());
  let observer: ResizeObserver | undefined;
  const anchor: TranscriptSelectionAnchor = {
    getRect() {
      const element = frame();
      if (!rect || !element?.isConnected || !element.clientWidth || !element.clientHeight) return null;
      const left = Math.max(0, rect.left), top = Math.max(0, rect.top);
      const right = Math.min(element.clientWidth, rect.left + rect.width), bottom = Math.min(element.clientHeight, rect.top + rect.height);
      if (right <= left || bottom <= top) return null;
      const bounds = element.getBoundingClientRect(), sx = bounds.width / element.offsetWidth, sy = bounds.height / element.offsetHeight;
      return { left: bounds.left + (element.clientLeft + left) * sx, top: bounds.top + (element.clientTop + top) * sy,
        width: (right - left) * sx, height: (bottom - top) * sy };
    },
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) {
        const element = frame();
        if (element && typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(notify); observer.observe(element); }
        watch(true);
      }
      return () => { listeners.delete(listener); if (!listeners.size) { observer?.disconnect(); observer = undefined; watch(false); } };
    },
  };
  return { anchor, update(next: SelectionRect | null) { rect = next; notify(); } };
}
export const sourceRangeSchema = z.object({ start: offset, end: offset }).strict().refine(range => range.end > range.start);
const selectionFields = {
  messageID: z.string().min(1).max(1024),
  selectedText: z.string().min(1).max(32_000).refine(text => !!text.trim()),
  sourceMarkdown: z.string().max(100_000).optional(),
  sourceRanges: z.array(sourceRangeSchema).min(1).max(100).optional(),
  renderedTextRanges: z.array(z.object({
    blockKey: z.string().min(1).max(2048), startUTF16Offset: offset,
    utf16Length: offset, selectedText: z.string().max(32_000),
  }).strict()).max(200).optional(),
};
const selectionSchema = z.object(selectionFields).strict();
export const selectionActionFields = {
  ...selectionFields,
  action: z.enum(['add', 'detail', 'side']),
  anchor: z.object({ id: z.string().min(1).max(128), rect: selectionRectSchema }).strict().optional(),
  selections: z.array(selectionSchema).min(1).max(20).optional(),
};
export const selectionActionSchema = z.object(selectionActionFields).strict();
export type SelectionActionPayload = z.infer<typeof selectionActionSchema>;
export interface TranscriptSelection {
  /** Original ChatMessage.id, never a merged presentation/article ID. */
  messageID: string;
  selectedText: string;
  sourceMarkdown?: string;
  sourceRanges?: { start: number; end: number }[];
}
export interface TranscriptSelectionAction extends TranscriptSelection {
  action: 'add' | 'detail' | 'side';
  anchor?: TranscriptSelectionAnchor;
  /** Present when an explicit DOM selection spans several original messages. */
  selections?: TranscriptSelection[];
}

/** Resolve source identity through our projection catalog. Never search for
 * selected text in a source string: repeated phrases and DIL are ambiguous. */
export function resolveSelectionAction(input: SelectionActionPayload, messages: ChatMessage[], payload: TranscriptPayload): TranscriptSelectionAction | null {
  const parsed = selectionActionSchema.safeParse(input);
  if (!parsed.success) return null;
  const data = parsed.data;
  const known = new Map(messages.map(message => [message.id, message]));
  const catalog = new Map(payload.blockCatalog.map(block => [block.id, block]));
  const selections: TranscriptSelection[] = [];
  for (const selection of data.selections || [data]) {
    const article = payload.messages.find(message => message.id === selection.messageID && message.role === 'assistant');
    if (!article || !Array.isArray(article.blocks)) return null;
    let cursor = 0, invalidSource = false;
    const blocks = article.blocks.flatMap(id => {
      const block = catalog.get(id);
      if (block?.type !== 'main_text' || typeof block.text !== 'string' || !block.text.trim()) return [];
      const message = known.get(String(block.sourceBlockId));
      if (!message || message.text !== block.text || message.role !== 'assistant') { invalidSource = true; return []; }
      const start = cursor; cursor += block.text.length + 2;
      return [{ id: block.id, message, start, end: start + block.text.length }];
    });
    if (!blocks.length || invalidSource) return null;
    const fullSource = blocks.map(block => block.message.text).join('\n\n');
    const ranges = selection.sourceRanges;
    if (ranges) {
      if (ranges.some((range, index) => range.end > fullSource.length || (index > 0 && range.start < ranges[index - 1].end))) return null;
      // The original script supplies the exact envelope, even for disjoint
      // ranges. It also binds offsets to the source version before streaming.
      if (selection.sourceMarkdown !== fullSource.slice(ranges[0].start, ranges.at(-1)!.end)) return null;
    }
    const rendered = selection.renderedTextRanges || [];
    if (rendered.some(range => !blocks.some(block => block.id === range.blockKey))) return null;
    const selectedBlocks = blocks.filter(block => ranges
      ? ranges.some(range => range.start < block.end && range.end > block.start)
      : rendered.length ? rendered.some(range => range.blockKey === block.id) : blocks.length === 1);
    if (!selectedBlocks.length) return null;
    for (const block of selectedBlocks) {
      const text = selectedBlocks.length === 1 ? selection.selectedText
        : rendered.filter(range => range.blockKey === block.id).map(range => range.selectedText).filter(text => text.trim()).join('\n');
      // Without the original per-block rendered selections, splitting would
      // incorrectly attribute a combined display string to one source.
      if (!text.trim()) return null;
      const localRanges = ranges?.filter(range => range.start < block.end && range.end > block.start)
        .map(range => ({ start: Math.max(range.start, block.start) - block.start, end: Math.min(range.end, block.end) - block.start }));
      selections.push({ messageID: block.message.id, selectedText: text,
        ...(localRanges?.length ? { sourceRanges: localRanges,
          sourceMarkdown: block.message.text.slice(localRanges[0].start, localRanges.at(-1)!.end) } : {}) });
    }
  }
  if (!selections.length || selections.length > 20 || selections.reduce((size, item) => size + item.selectedText.length, 0) > 32_000) return null;
  return { ...selections[0], action: data.action, ...(selections.length > 1 ? { selectedText: selections.map(item => item.selectedText).join('\n'), selections } : {}) };
}
